<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useQuasar } from 'quasar'
import { storeToRefs } from 'pinia'
import { ModerationStatus } from '@roadfootage/api-client'
import type { ViewIncidentReportResponse } from '@roadfootage/api-client'
import { useCreatorStore } from '../stores/creator'
import { useUserStore } from '../stores/user/user.store'
import FileProcessingStatus from '../components/FileProcessingStatus.vue'
import { useShare } from '../composables/useShare'
import { formatIncidentDateTime } from '../utils/format'

const creatorStore = useCreatorStore()
const { incidentReportList, processingByReport, removingFileId, focused, focusedMissing } =
  storeToRefs(creatorStore)
const route = useRoute()
const router = useRouter()
const { isRestricted } = storeToRefs(useUserStore())
const $q = useQuasar()
const { shareIncident } = useShare()

const focusedId = (): string | undefined => {
  const value = route.query.report
  return typeof value === 'string' && value ? value : undefined
}

onMounted(async () => {
  creatorStore.$reset()
  const loadingFocused = creatorStore.loadFocused(focusedId())
  await creatorStore.fetchCreatorIncidentReports()
  await loadingFocused
})

// The "earlier report" link from a duplicate hint changes only the query, so the view stays mounted.
watch(
  () => route.query.report,
  () => creatorStore.loadFocused(focusedId())
)

const closeFocused = () => router.replace({ name: 'uploads', query: {} })

onUnmounted(() => creatorStore.stopProcessingPoll())

// --- Formatters & Helpers ---

const formatIncidentDate = (date?: Date): string => formatIncidentDateTime(date, null)

const getStatusColor = (status?: string): string => {
  switch (status) {
    case 'ActionRequired':
      return 'red'
    case 'Pending':
      return 'blue-8'
    case 'Approved':
      return 'green-9'
    case 'Rejected':
      return 'grey-8'
    default:
      return 'blue-grey-4'
  }
}

const getStatusLabel = (status?: string): string => {
  if (status === 'ActionRequired') return 'Action Required'
  return status || 'Processing'
}

// --- Interactions ---

// Only a pending report with more than one file can lose a file. The API enforces the same rules.
const canRemoveFiles = (incident: ViewIncidentReportResponse): boolean =>
  incident.moderationStatus === ModerationStatus.Pending &&
  (processingByReport.value[incident.id!]?.length ?? 0) > 1

const removeErrorByReport = ref<Record<string, string>>({})

const onRemoveFile = async (reportId: string, fileId: string) => {
  removeErrorByReport.value[reportId] = ''
  const result = await creatorStore.removeFile(reportId, fileId)
  if (result.ok) {
    $q.notify({ type: 'positive', message: 'File removed' })
    return
  }
  removeErrorByReport.value[reportId] = result.message
}

const onPullToRefresh = async (done: () => void) => {
  creatorStore.$reset()
  await creatorStore.fetchCreatorIncidentReports()
  done()
}

const onLoadMore = async (index: number, done: (stop?: boolean) => void) => {
  if (!creatorStore.hasMore) {
    done(true) // Stop infinite scroll
    return
  }
  await creatorStore.fetchCreatorIncidentReports()
  if (!creatorStore.hasMore) {
    done(true) // Stop infinite scroll
  } else {
    done()
  }
}

const handleTouchPlay = (event: Event) => {
  const video = event.target as HTMLVideoElement
  video.paused ? video.play() : video.pause()
}
</script>

<template>
  <q-page padding>
    <div class="row q-mb-md items-start q-col-gutter-y-sm">
      <div class="col-12 col-sm">
        <h4 class="text-h4 text-weight-bold q-my-none text-blue-grey-9">Your Footage</h4>
      </div>
      <div v-if="!isRestricted" class="col-12 col-sm-auto">
        <q-btn
          unelevated
          color="blue-8"
          icon="mdi-video-plus"
          label="Share Footage"
          :to="{ name: 'upload' }"
        />
      </div>
    </div>

    <q-card v-if="focused" flat bordered class="bg-white q-mb-md" data-testid="focused-report">
      <q-card-section class="row items-start no-wrap">
        <div class="col">
          <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Earlier upload</div>
          <div class="row items-center no-wrap">
            <q-img
              v-if="focused.thumbnailUrl"
              :src="focused.thumbnailUrl"
              width="96px"
              ratio="1.78"
              class="q-mr-sm rounded-borders"
              fit="cover"
            />
            <div>
              <div class="text-body2 text-weight-bold text-blue-grey-9">
                {{ focused.address?.formattedAddress || 'Location processing...' }}
              </div>
              <div class="text-caption text-blue-grey-8 q-mt-xs">
                {{ formatIncidentDate(focused.incidentDateTime ?? undefined) }}
                <q-badge
                  :color="getStatusColor(focused.moderationStatus)"
                  class="text-weight-bold q-ml-xs"
                >
                  {{ getStatusLabel(focused.moderationStatus) }}
                </q-badge>
              </div>
            </div>
          </div>
        </div>
        <q-btn
          flat
          round
          dense
          icon="mdi-close"
          aria-label="Close earlier upload"
          data-testid="focused-report-close"
          @click="closeFocused"
        />
      </q-card-section>
    </q-card>

    <q-banner
      v-else-if="focusedMissing"
      dense
      rounded
      class="bg-blue-grey-1 text-blue-grey-9 q-mb-md"
      data-testid="focused-report-missing"
    >
      That report is no longer available.
      <template #action>
        <q-btn
          flat
          round
          dense
          icon="mdi-close"
          aria-label="Dismiss"
          data-testid="focused-report-close"
          @click="closeFocused"
        />
      </template>
    </q-banner>

    <q-pull-to-refresh @refresh="onPullToRefresh" color="blue-8">
      <q-inner-loading :showing="creatorStore.loading && !incidentReportList.length" />

      <div
        v-if="!incidentReportList.length && !creatorStore.loading"
        class="text-center text-blue-grey-8 q-pa-xl"
      >
        <q-icon name="mdi-video-off" size="4rem" class="q-mb-md" />
        <div class="text-h6">No footage yet</div>
        <div class="text-body2 q-mt-sm">
          Your dashboard will populate once you share your first incident.
        </div>
      </div>

      <q-infinite-scroll v-else @load="onLoadMore" :offset="250">
        <div class="row q-col-gutter-md">
          <div
            v-for="incident in incidentReportList"
            :key="incident.id"
            class="col-12 col-sm-6 col-lg-4"
          >
            <q-card flat bordered class="bg-white">
              <div class="relative-position overflow-hidden" style="padding-top: 56.25%">
                <div v-if="!incident.thumbnailUrl && !incident.previewUrl" class="absolute-full">
                  <div class="lava-lamp-bg full-width full-height flex flex-center">
                    <div class="column items-center">
                      <q-spinner-rings color="white" size="3em" />
                      <span
                        class="text-white text-caption q-mt-sm text-weight-bold text-uppercase tracking-wide"
                        >Processing</span
                      >
                    </div>
                  </div>
                </div>

                <video
                  v-else-if="incident.previewUrl"
                  :src="incident.previewUrl"
                  :poster="incident.thumbnailUrl || undefined"
                  class="absolute-full fit-cover block"
                  muted
                  loop
                  playsinline
                  @mouseenter="($event.target as HTMLVideoElement).play()"
                  @mouseleave="($event.target as HTMLVideoElement).pause()"
                  @touchstart="handleTouchPlay"
                />

                <q-img
                  v-else-if="incident.thumbnailUrl"
                  :src="incident.thumbnailUrl"
                  class="absolute-full"
                  fit="cover"
                />
              </div>

              <q-card-section class="q-pb-xs">
                <div class="row items-start no-wrap">
                  <div class="col">
                    <div class="text-body2 text-weight-bold text-blue-grey-9 ellipsis">
                      {{ incident.address?.formattedAddress || 'Location processing...' }}
                    </div>
                    <div class="text-caption text-blue-grey-6 q-mt-xs">
                      <q-icon name="mdi-clock-outline" size="xs" class="q-mr-xs" />
                      {{ formatIncidentDate(incident.incidentDateTime ?? undefined) }}
                    </div>
                  </div>
                  <q-badge
                    :color="getStatusColor(incident.moderationStatus)"
                    class="text-weight-bold q-ml-sm q-mt-xs"
                  >
                    {{ getStatusLabel(incident.moderationStatus) }}
                  </q-badge>
                </div>
              </q-card-section>

              <q-separator />

              <q-card-actions
                v-if="incident.id && incident.moderationStatus === ModerationStatus.Approved"
                class="q-px-sm q-py-xs"
              >
                <q-btn
                  flat
                  dense
                  size="sm"
                  color="primary"
                  icon="mdi-share-variant"
                  label="Share"
                  data-testid="share-incident"
                  @click="shareIncident(incident.id)"
                />
              </q-card-actions>

              <q-card-section
                v-if="
                  processingByReport[incident.id!]?.length ||
                  incident.moderationStatus === ModerationStatus.Rejected
                "
                class="q-pa-sm"
                data-testid="incident-processing"
              >
                <FileProcessingStatus
                  :files="processingByReport[incident.id!]"
                  :rejected="incident.moderationStatus === ModerationStatus.Rejected"
                  :removable="canRemoveFiles(incident)"
                  :removing-file-id="removingFileId"
                  @remove="(fileId) => onRemoveFile(incident.id!, fileId)"
                />
                <div
                  v-if="removeErrorByReport[incident.id!]"
                  class="text-caption text-negative q-mt-xs"
                  data-testid="remove-file-error"
                >
                  {{ removeErrorByReport[incident.id!] }}
                </div>
              </q-card-section>

              <q-card-section class="q-pa-sm">
                <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Your description</div>
                <div class="text-caption text-blue-grey-7 ellipsis-2-lines">
                  {{ incident.userSubmittedIncidentDescription || 'No description provided.' }}
                </div>
              </q-card-section>

              <q-card-section
                v-if="incident.incidentDescription"
                class="q-pa-sm border-left-accent bg-grey-2"
              >
                <div class="text-subtitle2 text-blue-grey-8 q-mb-xs">Moderated description</div>
                <div class="text-caption text-blue-grey-9 ellipsis-2-lines">
                  {{ incident.incidentDescription }}
                </div>
              </q-card-section>
            </q-card>
          </div>
        </div>

        <template v-slot:loading>
          <div class="row justify-center q-my-md">
            <q-spinner-dots color="blue-8" size="40px" />
          </div>
        </template>
      </q-infinite-scroll>
    </q-pull-to-refresh>
  </q-page>
</template>

<style scoped lang="scss">
.media-col {
  min-height: 220px;
}

.fit-cover {
  object-fit: cover;
}
.fit-contain {
  object-fit: contain;
}
.line-height-tight {
  line-height: 1.2;
}
.tracking-wide {
  letter-spacing: 1px;
}

.border-left-accent {
  border-left: 4px solid $primary;
}

.border-top {
  border-top: 1px solid #e0e0e0;
}
</style>
